import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Pencil, Trash2, BookMarked, Info, Search,
} from "lucide-react";
import {
  listStyleExamples, createStyleExample, updateStyleExample, deleteStyleExample,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MobileMenu, MobileTabBar } from "@/components/AppNav";
import { AppHeader } from "@/components/brand/AppHeader";
import { BrandName, BrandShort } from "@/lib/brand";

const EMPTY = { name: "", category: "", text: "", active: true, notes: "" };

const CATEGORIES = [
  "Pedigree / Breeding", "Rope Horse", "Ranch Horse", "Barrel Horse",
  "Prospect", "Finished Horse", "Young Horse", "Broodmare", "Stallion", "General",
];

export default function StyleLibrary() {
  const navigate = useNavigate();
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id or null (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setExamples(await listStyleExamples());
    } catch {
      toast.error("Could not load the Ad Style Library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return examples;
    return examples.filter(
      (e) => (e.name || "").toLowerCase().includes(q) ||
             (e.category || "").toLowerCase().includes(q) ||
             (e.text || "").toLowerCase().includes(q)
    );
  }, [examples, search]);

  const activeCount = examples.filter((e) => e.active).length;

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (e) => {
    setEditing(e.id);
    setForm({ name: e.name || "", category: e.category || "", text: e.text || "", active: !!e.active, notes: e.notes || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Example Name and Final Advertisement Text are required");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, category: form.category || null, notes: form.notes || null };
      if (editing) {
        const updated = await updateStyleExample(editing, body);
        setExamples((xs) => xs.map((x) => (x.id === editing ? updated : x)));
        toast.success("Example updated");
      } else {
        const created = await createStyleExample(body);
        setExamples((xs) => [created, ...xs]);
        toast.success("Example added");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Could not save the example");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (e) => {
    try {
      const updated = await updateStyleExample(e.id, {
        name: e.name, category: e.category, text: e.text, active: !e.active, notes: e.notes,
      });
      setExamples((xs) => xs.map((x) => (x.id === e.id ? updated : x)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await deleteStyleExample(id);
      setExamples((xs) => xs.filter((x) => x.id !== id));
      toast.success("Example deleted");
    } catch {
      toast.error("Could not delete the example");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-0 overflow-x-clip">
      <AppHeader title="Ad Style Library" onBack={() => navigate("/")} backTestId="back-to-horses" width="max-w-6xl">
          <div className="flex items-center gap-2 shrink-0">
          <Button
            data-testid="add-example-button"
            onClick={openNew}
            className="h-11 w-11 sm:w-auto sm:h-10 p-0 sm:px-5 bg-amber-800 hover:bg-amber-900 text-white rounded-full shrink-0"
            aria-label="Add Example"
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Example</span>
          </Button>
          <MobileMenu />
          </div>
      </AppHeader>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex items-start gap-2 text-sm text-stone-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
          <Info className="h-4 w-4 mt-0.5 text-amber-800 shrink-0" />
          <p>
            These approved ads teach the AI <strong>writing style only</strong> (tone, structure, hooks, pacing).
            They are <strong>never</strong> used as facts for another horse. Active examples guide
            <strong> Version 3 — <BrandShort /> Style</strong> generation.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              data-testid="style-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search examples…"
              className="pl-9 border-stone-200 focus-visible:ring-amber-800"
            />
          </div>
          <span className="text-xs text-stone-500 whitespace-nowrap">
            {activeCount} active · {examples.length} total
          </span>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="empty-library" className="text-center py-20 text-stone-500">
            <BookMarked className="h-10 w-10 mx-auto mb-3 text-stone-300" />
            <p className="font-medium">No style examples yet.</p>
            <p className="text-sm">Add a finalized <BrandName /> ad to teach the AI your style.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {filtered.map((e) => (
              <div
                key={e.id}
                data-testid={`style-card-${e.id}`}
                className={`min-w-0 rounded-xl border p-4 bg-white ${e.active ? "border-amber-300" : "border-stone-200 opacity-75"}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-stone-900 truncate">{e.name}</h3>
                    {e.category && (
                      <Badge variant="outline" className="mt-1 border-amber-300 text-amber-800 text-[11px]">
                        {e.category}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Switch
                      data-testid={`toggle-active-${e.id}`}
                      checked={e.active}
                      onCheckedChange={() => toggleActive(e)}
                    />
                    <span className="text-[11px] text-stone-500 w-12">{e.active ? "Active" : "Inactive"}</span>
                  </div>
                </div>
                <p className="text-sm text-stone-600 whitespace-pre-wrap line-clamp-4 leading-relaxed">
                  {e.text}
                </p>
                {e.notes && <p className="mt-2 text-xs text-stone-400 italic">Note: {e.notes}</p>}
                <div className="mt-3 flex justify-end gap-2 border-t border-stone-100 pt-3">
                  <Button
                    data-testid={`edit-example-${e.id}`}
                    variant="outline" size="sm"
                    onClick={() => openEdit(e)}
                    className="h-11 sm:h-9 rounded-full border-stone-300 text-stone-700 hover:bg-stone-50"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    data-testid={`delete-example-${e.id}`}
                    variant="outline" size="sm"
                    onClick={() => setDeleteId(e.id)}
                    className="h-11 sm:h-9 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <MobileTabBar />

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] rounded-xl max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="example-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Example" : "Add Example"}</DialogTitle>
            <DialogDescription>Paste a finalized <BrandName /> ad. Stored application-side only.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Example Name *</label>
              <Input
                data-testid="example-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Smooth As A Cat weanling — strong pedigree ad"
                className="border-stone-200 focus-visible:ring-amber-800"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Horse Type / Category</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`cat-${c}`}
                    onClick={() => setForm((f) => ({ ...f, category: f.category === c ? "" : c }))}
                    className={`text-xs rounded-full px-3 py-2 sm:py-1 border transition-colors ${
                      form.category === c
                        ? "bg-amber-800 text-white border-amber-800"
                        : "bg-white text-stone-600 border-stone-300 hover:border-amber-400"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Final Advertisement Text *</label>
              <Textarea
                data-testid="example-text"
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                rows={10}
                placeholder="Paste the finalized ad copy here…"
                className="border-stone-200 focus-visible:ring-amber-800 text-sm"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Notes (optional)</label>
              <Input
                data-testid="example-notes"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Why this is a good style reference…"
                className="border-stone-200 focus-visible:ring-amber-800"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="example-active"
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              />
              <span className="text-sm text-stone-600">Active (available to guide Version 3)</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="cancel-example-button" variant="outline" onClick={() => setDialogOpen(false)} className="h-11 sm:h-10 rounded-full">Cancel</Button>
            <Button
              data-testid="save-example-button"
              onClick={save}
              disabled={saving}
              className="h-11 sm:h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white"
            >
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Example"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog" className="w-[calc(100%-1.5rem)] rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this style example?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes it from the Ad Style Library. It won't affect any horse's advertisement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
